(() => {
  const PREVIEW_SELECTOR = '[data-vanscape-cart-preview]';

  function normalizeColor(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u00df/g, 'ss');
  }

  function resolveColorHex(value) {
    const normalized = normalizeColor(value);
    const directHex = normalized.match(/#([0-9a-f]{6})/i);
    if (directHex) return `#${directHex[1]}`;

    const rules = [
      [/navy blazer|navy-blazer/, '#171f2c'],
      [/charcoal heather|anthrazit meliert/, '#463e3d'],
      [/vintage black|vintage schwarz/, '#43413d'],
      [/team royal|team konigsblau|team koenigsblau/, '#1b43ae'],
      [/forest green|waldgrun|waldgruen/, '#1a3b23'],
      [/turf green|rasengrun|rasengruen/, '#187532'],
      [/military green|militargrun|militaergruen/, '#5c4f32'],
      [/carolina blue|carolina-blau/, '#98b7ee'],
      [/tropical blue|tropisches blau/, '#00a0b3'],
      [/carbon grey|carbon gray|carbongrau|karbongrau/, '#c7c3be'],
      [/graphite heather|graphit heather|graphit meliert/, '#858585'],
      [/light pink|hellrosa|hellpink/, '#ffd0cc'],
      [/sky blue|himmelblau/, '#d6edf7'],
      [/oatmeal heather|hafer meliert/, '#fbf2e1'],
      [/maroon|kastanienbraun|weinrot|bordeaux/, '#47001b'],
      [/black|schwarz/, '#141313'],
      [/purple|lila|violett/, '#48197d'],
      [/cardinal|kardinal/, '#c21b3a'],
      [/royal|konigsblau|koenigsblau/, '#175ac7'],
      [/sapphire|saphir/, '#007ab3'],
      [/heliconia|helikonie/, '#e33f7a'],
      [/azalea|azalee/, '#ff98c6'],
      [/white|weiss/, '#ffffff'],
      [/anthracite|anthrazit/, '#3e383e'],
      [/khaki/, '#6e6b4d'],
      [/desert dust|wustenstaub|wuestenstaub/, '#dcccb4'],
      [/lavender|lavendel/, '#f7ecff'],
      [/navy|marineblau|dunkelblau/, '#16263f'],
      [/charcoal|holzkohle/, '#74716b'],
      [/gold/, '#ffad20'],
      [/sport grey|sport gray|sportgrau/, '#d1d1d1'],
      [/light blue|hellblau/, '#c1d5e6'],
      [/natural|naturlich/, '#f8eddc'],
      [/sand/, '#d6c0ab'],
      [/himmel|sky/, '#8ee0ff'],
      [/asche|ash/, '#f3f3f3'],
      [/dunkles heather/, '#595959'],
      [/stargazer/, '#3d737d'],
      [/bone/, '#f5e8ce'],
      [/heather|meliert/, '#babdc0'],
      [/pink|rosa/, '#ffb2da'],
      [/grey|gray|grau/, '#d1d1d1'],
      [/blue|blau/, '#3f7ec4'],
      [/green|grun|gruen/, '#1a5a32'],
      [/red|rot/, '#9f1d2f'],
      [/yellow|gelb/, '#ffad20']
    ];

    return rules.find(([pattern]) => pattern.test(normalized))?.[1] || '#d1d5db';
  }

  const retryTimers = new WeakMap();
  const RETRY_DELAYS = [250, 900, 2500];

  function getRenderKey(previewEl) {
    return [
      previewEl.dataset.productHandle,
      previewEl.dataset.variantTitle,
      previewEl.dataset.selectedColorHex || previewEl.dataset.selectedColor,
      previewEl.dataset.selectedSize,
      previewEl.dataset.designImage,
      previewEl.dataset.jobId
    ].join('|');
  }

  function hideLooseLegacyLayers(previewEl) {
    previewEl.querySelectorAll('.vs-preview-color-layer').forEach((layerEl) => layerEl.remove());
    previewEl.querySelectorAll('[data-vanscape-cart-preview-overlay], .vs-design-overlay').forEach((overlayEl) => {
      overlayEl.style.setProperty('display', 'none', 'important');
      overlayEl.removeAttribute('src');
    });
  }

  async function refreshDesignImageFromJob(previewEl) {
    const jobId = previewEl.dataset.jobId || '';
    if (!jobId || previewEl.dataset.jobRefreshAttempted === 'true') return false;
    previewEl.dataset.jobRefreshAttempted = 'true';

    try {
      const response = await fetch(`/apps/vanscape/jobs/${encodeURIComponent(jobId)}`, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
        cache: 'no-store'
      });
      if (!response.ok) return false;

      const data = await response.json().catch(() => ({}));
      const result = data?.result || data;
      const thumbUrl = result?.thumb_url || data?.thumb_url || '';
      if (!thumbUrl) return false;

      previewEl.dataset.designImage = thumbUrl;
      return true;
    } catch {
      return false;
    }
  }

  function showFallback(previewEl, imgEl) {
    hideLooseLegacyLayers(previewEl);
    previewEl.querySelectorAll('.vs-preview-composite-canvas').forEach((canvasEl) => {
      canvasEl.style.setProperty('display', 'none', 'important');
    });
    imgEl.style.removeProperty('display');
    imgEl.style.removeProperty('visibility');
    previewEl.dataset.vanscapeCartPreviewStatus = 'fallback';
    previewEl.classList.remove('is-rendered');
  }

  function scheduleRetry(previewEl, attempt) {
    const delay = RETRY_DELAYS[attempt];
    if (delay == null || retryTimers.has(previewEl)) return false;

    const timer = window.setTimeout(() => {
      retryTimers.delete(previewEl);
      renderPreview(previewEl, attempt + 1);
    }, delay);
    retryTimers.set(previewEl, timer);
    return true;
  }

  async function renderPreview(previewEl, attempt = 0) {
    if (!previewEl?.isConnected) return;

    const imgEl = previewEl.querySelector('[data-vanscape-cart-preview-base]');
    const placeholderEl = previewEl.querySelector('[data-vanscape-cart-preview-placeholder]');
    if (!imgEl || !placeholderEl) return;

    hideLooseLegacyLayers(previewEl);

    const renderKey = getRenderKey(previewEl);
    const status = previewEl.dataset.vanscapeCartPreviewStatus;
    if (status === 'ready' && previewEl.dataset.vanscapeCartPreviewKey === renderKey) return;
    if (status === 'rendering' && previewEl.dataset.vanscapeCartPreviewKey === renderKey && attempt === 0) return;

    const generator = window.VanscapeMockupGenerator;
    if (!generator?.renderProductPreview) {
      scheduleRetry(previewEl, attempt);
      return;
    }

    previewEl.dataset.vanscapeCartPreviewStatus = 'rendering';
    previewEl.dataset.vanscapeCartPreviewKey = renderKey;
    if (!previewEl.classList.contains('is-rendered')) {
      imgEl.style.visibility = 'hidden';
    }

    try {
      const rendered = await generator.renderProductPreview({
        productHandle: previewEl.dataset.productHandle || '',
        productTitle: previewEl.dataset.productTitle || '',
        variantTitle: previewEl.dataset.variantTitle || '',
        selectedSize: previewEl.dataset.selectedSize || '',
        productImageUrl: previewEl.dataset.productImage || '',
        designThumbUrl: previewEl.dataset.designImage || '',
        selectedColorHex: resolveColorHex(
          previewEl.dataset.selectedColorHex || previewEl.dataset.selectedColor
        ),
        imgEl,
        placeholderEl
      });

      if (!rendered) throw new Error('Kein passendes Produkt-Mockup gefunden.');

      hideLooseLegacyLayers(previewEl);
      imgEl.style.removeProperty('visibility');
      previewEl.dataset.vanscapeCartPreviewStatus = 'ready';
      previewEl.dataset.vanscapeCartPreviewKey = getRenderKey(previewEl);
      previewEl.classList.add('is-rendered');
    } catch (error) {
      const refreshed = await refreshDesignImageFromJob(previewEl);
      if (refreshed) {
        previewEl.dataset.vanscapeCartPreviewStatus = '';
        renderPreview(previewEl, attempt);
        return;
      }

      previewEl.dataset.vanscapeCartPreviewStatus = '';
      if (scheduleRetry(previewEl, attempt)) return;

      showFallback(previewEl, imgEl);
      console.warn('Personalisierte Warenkorb-Vorschau konnte nicht gerendert werden.', error);
    }
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches(PREVIEW_SELECTOR)) {
      renderPreview(root);
    }
    root.querySelectorAll?.(PREVIEW_SELECTOR).forEach(renderPreview);
  }

  function scanAddedNodes(records) {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  }

  scan();
  document.addEventListener('DOMContentLoaded', () => scan());
  window.addEventListener('load', () => scan());
  document.addEventListener('cart:refresh', () => window.setTimeout(() => scan(), 0));
  document.addEventListener('cart-drawer:refreshed', (event) => scan(event.target));
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  const observer = new MutationObserver(scanAddedNodes);
  observer.observe(document.body, { childList: true, subtree: true });
})();
